import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { canAccess, PLAN_LABELS, getInitials } from '../../utils/helpers';
import { showToast } from '../common/Toast';

const Dashboard = () => {
  const { user, logout, updateProfile, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    organization: '',
    country: '',
    role: 'Chercheur',
  });
  const [profileMsg, setProfileMsg] = useState('');

  useEffect(() => {
    if (!user) return;
    refreshUser();
    setFormData({
      full_name: user.full_name || '',
      organization: user.organization || '',
      country: user.country || '',
      role: user.role || 'Chercheur',
    });
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProfileMsg('');

    try {
      await updateProfile(formData);
      setProfileMsg('Profil mis à jour avec succès.');
      showToast('Profil mis à jour avec succès.');
    } catch (err) {
      setProfileMsg('Erreur lors de la mise à jour.');
      showToast('Erreur lors de la mise à jour.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  if (!user) {
    return (
      <div className="text-center" style={{ padding: '60px' }}>
        <p>Veuillez vous connecter pour accéder à votre espace.</p>
        <Link to="/connexion" className="btn btn-dark">Se connecter</Link>
      </div>
    );
  }

  return (
    <>
      <section className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Accueil</Link> / Mon compte
          </div>
          <h1>Mon espace AEII</h1>
        </div>
      </section>

      <section className="section">
        <div className="container dashboard-grid">
          <aside className="dashboard-side">
            <div className="avatar">{getInitials(user.full_name || user.email)}</div>
            <h3 className="text-center" style={{ marginBottom: '2px' }}>
              {user.full_name || 'Utilisateur AEII'}
            </h3>
            <p className="text-center" style={{ color: '#667180', fontSize: '0.85rem', marginBottom: '16px' }}>
              {user.email}
            </p>
            <div className="text-center" style={{ marginBottom: '20px' }}>
              <span
                className="plan-badge"
                style={{ 
                  color: '#fff', 
                  padding: '6px 16px', 
                  background: user.plan === 'gratuit' ? '#3f7a52' : user.plan === 'classique' ? '#1d5f8a' : '#b8862f' 
                }}
              >
                {PLAN_LABELS[user.plan] || user.plan}
              </span>
            </div>
            <Link to="/publications" className="btn btn-ghost btn-block" style={{ marginBottom: '10px' }}>
              Explorer la bibliothèque
            </Link>
            <Link to="/abonnements" className="btn btn-dark btn-block" style={{ marginBottom: '10px' }}>
              Gérer mon abonnement
            </Link>
            <button
              className="btn btn-outline btn-block"
              onClick={handleLogout}
              style={{ color: '#0c2340', borderColor: '#e1e5eb' }}
            >
              Se déconnecter
            </button>
          </aside>

          <div>
            <div className="dashboard-panel">
              <h3>Mon abonnement</h3>
              <div className="plan-current">
                <div>
                  <strong>Formule {PLAN_LABELS[user.plan] || user.plan}</strong>
                  <p style={{ margin: '4px 0 0', color: '#667180', fontSize: '0.85rem' }}>
                    {user.plan === 'gratuit'
                      ? 'Accès aux publications en libre accès uniquement.'
                      : `Actif jusqu'au ${user.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString('fr-FR') : '—'}`}
                  </p>
                </div>
                <Link to="/abonnements" className="btn btn-primary btn-sm">
                  Changer de formule
                </Link>
              </div>
            </div>

            <div className="dashboard-panel">
              <h3>Informations du profil</h3>
              {profileMsg && (
                <div className={`form-msg ${profileMsg.includes('succès') ? 'success' : 'error'}`}>
                  {profileMsg}
                </div>
              )}
              <form onSubmit={handleProfileUpdate}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="full_name">Nom complet</label>
                    <input
                      type="text"
                      id="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" value={user.email} disabled />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="organization">Organisation</label>
                    <input
                      type="text"
                      id="organization"
                      value={formData.organization}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="country">Pays</label>
                    <input
                      type="text"
                      id="country"
                      value={formData.country}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="role">Profil</label>
                  <select id="role" value={formData.role} onChange={handleChange}>
                    <option value="Chercheur">Chercheur</option>
                    <option value="Étudiant">Étudiant</option>
                    <option value="Entreprise">Entreprise</option>
                    <option value="Institution publique">Institution publique</option>
                    <option value="Journaliste">Journaliste</option>
                    <option value="Consultant">Consultant</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <button type="submit" className="btn btn-dark" disabled={loading}>
                  {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </form>
            </div>

            <div className="dashboard-panel">
              <h3>Accès selon votre formule</h3>
              <ul className="doc-meta-list">
                <li>
                  <span>Publications "Gratuit"</span>
                  <span>
                    <i className="fa-solid fa-check" style={{ color: '#1f8a4c' }} /> Toujours accessibles
                  </span>
                </li>
                <li>
                  <span>Publications "Classique"</span>
                  <span>
                    {canAccess(user.plan, 'classique') ? (
                      // ✅ Correction: le texte est à l'intérieur d'un fragment
                      <><i className="fa-solid fa-check" style={{ color: '#1f8a4c' }} /> Accès complet</>
                    ) : (
                      <><i className="fa-solid fa-lock" style={{ color: '#667180' }} /> Non inclus</>
                    )}
                  </span>
                </li>
                <li>
                  <span>Publications "Premium"</span>
                  <span>
                    {canAccess(user.plan, 'premium') ? (
                      <><i className="fa-solid fa-check" style={{ color: '#1f8a4c' }} /> Accès complet</>
                    ) : (
                      <><i className="fa-solid fa-lock" style={{ color: '#667180' }} /> Non inclus</>
                    )}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Dashboard;